import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@vnetwork/ui/primitives';
import type { AuditLog } from '@/api/audit';
import styles from './AuditLogDetailDialog.module.css';

function tryPrettyJson(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null;
  return (
    <div className={styles.infoRow}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

function JsonSection({ label, value }: { label: string; value: string | null | undefined }) {
  const pretty = tryPrettyJson(value);
  if (!pretty) return null;
  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>{label}</div>
      <pre className={styles.codeBlock}>{pretty}</pre>
    </div>
  );
}

/**
 * Side-modal showing full audit log detail: metadata, error, old/new JSON values,
 * IP and user-agent. Opened from the eye-icon in the DataTable row.
 */
export function AuditLogDetailDialog({
  log,
  onClose,
}: {
  log: AuditLog | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!log} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Audit log detail</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {log && (
            <div className={styles.body}>
              <InfoRow label="Time" value={log.created_at} />
              <InfoRow label="Actor" value={log.actor_email || log.actor_id} />
              {/* Only present when a DataCentral staff member performed the action
                  while impersonating this customer account. */}
              <InfoRow label="Performed by (staff)" value={log.impersonator_email} />
              <InfoRow label="Action" value={log.action} />
              <InfoRow label="Resource type" value={log.resource_type} />
              <InfoRow label="Resource" value={log.resource_name || log.resource_id} />
              <InfoRow label="Status" value={log.status} />

              {log.error_message && (
                <>
                  <div className={styles.divider} />
                  <div className={styles.section}>
                    <div className={styles.sectionLabel}>Error</div>
                    <div className={styles.errorBlock}>{log.error_message}</div>
                  </div>
                </>
              )}

              {(log.old_value || log.new_value) && (
                <div className={styles.divider} />
              )}
              <JsonSection label="Previous value" value={log.old_value} />
              <JsonSection label="New value" value={log.new_value} />

              {(log.client_ip || log.user_agent) && (
                <div className={styles.divider} />
              )}
              <InfoRow label="Client IP" value={log.client_ip} />
              <InfoRow label="User agent" value={log.user_agent} />
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
